import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Label,
  useToast,
} from '@ais-ui/ui-core';
import { XIcon } from 'lucide-react';
import { useSaveSettings, WMSLayerInfo } from '@ais-ui/websockets';
import React, { useState } from 'react';
import {
  ToggleBaseMapInSettings,
  useWMSSettings,
} from '../../WMSLayers/useWMSSettings';
import { useSettings } from '../SettingsProvider';
import { useLocalization } from '@ais-ui/locale-management';
import { BaseLayerConfig } from './useBaseLayerConfigs';

interface BaseMapRemoveServiceProps {
  baseLayerConfig: BaseLayerConfig;
  setBaseLayerConfigs: React.Dispatch<React.SetStateAction<BaseLayerConfig[]>>;
  className?: string;
}

export const BaseMapRemoveService = ({
  baseLayerConfig,
  setBaseLayerConfigs,
  className,
}: BaseMapRemoveServiceProps) => {
  const { messages } = useLocalization();
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const { toast } = useToast();
  const { settings } = useSettings();
  const wmsSettings = useWMSSettings();
  const { mutate: mutateSettings } = useSaveSettings();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  async function onRemoveService(event: React.FormEvent) {
    event.preventDefault();
    setIsSubmitting(true);

    const updatedSettings = ToggleBaseMapInSettings(
      settings,
      wmsSettings,
      baseLayerConfig.id,
      false,
    );

    // Save the updated settings
    mutateSettings(updatedSettings, {
      onSuccess: () => {
        toast({
          title: messages.genericToastTitleSuccess,
          description: messages.settingsBaseMapSuccessRemoveMessage,
        });
        setIsSubmitting(false);
        setShowDialog(false);
        setBaseLayerConfigs((prevLayerConfigs) => {
          return prevLayerConfigs.filter(
            (config) => config.id !== baseLayerConfig.id,
          );
        });
      },
      onError: () => {
        toast({
          title: messages.genericToastTitleError,
          description: messages.settingsBaseMapFailedRemoveMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
        setShowDialog(false);
      },
    });
  }

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className={className}>
          <XIcon className="size-4" />
        </Button>
      </DialogTrigger>
      <DialogContent id="delete-wms-service-dialog">
        <DialogHeader className="space-y-4">
          <DialogTitle>{messages.settingsWmsDeleteTitle}</DialogTitle>
          <DialogDescription>
            {messages.settingsBaseMapDeleteWarning}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onRemoveService}>
          <div className="grid grid-cols-[auto_minmax(50%,_1fr)] gap-y-4 gap-x-2 items-baseline">
            <Label className="font-light">
              {messages.settingsBaseMapDeleteNameLabel}
            </Label>
            <Label htmlFor="wms-service-name">{baseLayerConfig.title}</Label>
          </div>
          <DialogFooter className="p-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowDialog(false)}
              disabled={isSubmitting}
            >
              {messages.genericCancel}
            </Button>
            <Button
              variant="destructive"
              type="submit"
              id="base-map-service-confirm-delete-button"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? messages.genericDeleting
                : messages.genericDelete}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
